import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Download, Copy } from 'lucide-react'
import type { SavedScenario } from '@creanizer/types'

interface ScenarioDetailDialogProps {
  scenario: SavedScenario | null
  open: boolean
  onClose: () => void
}

// 値を文字列に変換
function toStr(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    return value.map(v => toStr(v)).join(', ')
  }
  if (typeof value === 'object') {
    if ('text' in value && typeof (value as any).text === 'string') {
      return (value as any).text
    }
  }
  return ''
}

// キー名を日本語ラベルに変換
function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    title: 'タイトル', name: '名前', summary: '概要', description: '説明',
    genre: 'ジャンル', system: 'システム', playerCount: 'プレイ人数',
    playTime: 'プレイ時間', difficulty: '難易度', theme: 'テーマ',
    atmosphere: '雰囲気', introduction: '導入', hook: 'フック',
    scenes: 'シーン', npcs: 'NPC', items: 'アイテム', locations: 'ロケーション',
    events: 'イベント', handouts: 'ハンドアウト', climax: 'クライマックス',
    epilogue: 'エピローグ', ending: 'エンディング', gmNotes: 'GMノート',
    basicInfo: '基本情報', content: 'コンテンツ', structure: '構成',
    metadata: 'メタデータ', investigation: '調査', truth: '真相',
    objectives: '目的', rewards: '報酬', triggers: 'トリガー',
    role: '役割', type: 'タイプ', appearance: '外見', personality: '性格',
    background: '背景', motivation: '動機', secrets: '秘密',
  }
  return labels[key] || key
}

// オブジェクトをテキスト形式にフォーマット（再帰的）
function formatObjectAsText(obj: any, indent: number = 0): string {
  if (obj === null || obj === undefined) return ''
  const prefix = '  '.repeat(indent)

  if (typeof obj === 'string') return obj
  if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj)

  if (Array.isArray(obj)) {
    return obj.map((item, i) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return `${prefix}- ${item}`
      }
      const itemTitle = item.title || item.name || `項目${i + 1}`
      let text = `${prefix}■ ${itemTitle}\n`
      text += formatObjectAsText(item, indent + 1)
      return text
    }).join('\n')
  }

  if (typeof obj === 'object') {
    return Object.entries(obj)
      .filter(([_, v]) => v !== null && v !== undefined && v !== '')
      .map(([key, value]) => {
        const label = keyToLabel(key)
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          return `${prefix}${label}: ${value}`
        }
        if (Array.isArray(value) && value.length === 0) return ''
        if (typeof value === 'object' && Object.keys(value).length === 0) return ''
        return `${prefix}【${label}】\n${formatObjectAsText(value, indent + 1)}`
      })
      .filter(Boolean)
      .join('\n')
  }

  return String(obj)
}

// シナリオ全体をテキスト形式にフォーマット
function formatScenarioAsText(scenario: SavedScenario): string {
  const r = scenario.result
  if (!r) return 'シナリオデータがありません'

  const title = r.title || r.basicInfo?.title || scenario.title || 'タイトルなし'
  let text = `${'='.repeat(60)}\n${toStr(title)}\n${'='.repeat(60)}\n\n`

  // すべてのトップレベルフィールドを出力
  Object.entries(r).forEach(([key, value]) => {
    if (value === null || value === undefined) return
    if (key === 'metadata') return // メタデータはスキップ
    if (Array.isArray(value) && value.length === 0) return
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return

    text += `${'─'.repeat(40)}\n`
    text += `【${keyToLabel(key)}】\n`
    text += `${'─'.repeat(40)}\n`
    text += formatObjectAsText(value, 0) + '\n\n'
  })

  return text
}

// 値をReactノードとして表示
function RenderValue({ value, depth = 0 }: { value: any; depth?: number }): JSX.Element | null {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{value}</p>
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="text-sm">{String(value)}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null
    // 単純な配列（文字列や数値）
    if (value.every(v => typeof v === 'string' || typeof v === 'number')) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => <Badge key={i} variant="secondary">{String(v)}</Badge>)}
        </div>
      )
    }
    // オブジェクトの配列
    return (
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="p-2 bg-gray-50 rounded border-l-2 border-blue-300">
            {(item.title || item.name) && (
              <h5 className="font-medium text-sm mb-1">{toStr(item.title || item.name)}</h5>
            )}
            <RenderValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    )
  }

  if (typeof value === 'object') {
    if ('text' in value && typeof value.text === 'string') {
      return <p className="text-sm text-gray-700 whitespace-pre-wrap">{value.text}</p>
    }

    const entries = Object.entries(value).filter(([k, v]) => {
      if (v === null || v === undefined || v === '') return false
      if (k === 'title' || k === 'name') return false // 既に表示済み
      if (Array.isArray(v) && v.length === 0) return false
      return true
    })

    if (entries.length === 0) return null

    return (
      <div className={depth > 0 ? 'pl-2 space-y-1' : 'space-y-2'}>
        {entries.map(([key, val]) => (
          <div key={key}>
            {typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean' ? (
              <p className="text-sm"><span className="text-gray-500">{keyToLabel(key)}:</span> {String(val)}</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 font-medium">{keyToLabel(key)}</p>
                <RenderValue value={val} depth={depth + 1} />
              </>
            )}
          </div>
        ))}
      </div>
    )
  }

  return <span>{String(value)}</span>
}

export function ScenarioDetailDialog({ scenario, open, onClose }: ScenarioDetailDialogProps) {
  if (!scenario) return null

  const handleDownloadText = () => {
    const text = formatScenarioAsText(scenario)
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const title = scenario.result?.title || scenario.result?.basicInfo?.title || scenario.title || 'scenario'
    a.download = `${toStr(title)}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadJson = () => {
    const json = JSON.stringify(scenario.result, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const title = scenario.result?.title || scenario.result?.basicInfo?.title || scenario.title || 'scenario'
    a.download = `${toStr(title)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleCopyText = () => {
    const text = formatScenarioAsText(scenario)
    navigator.clipboard.writeText(text)
  }

  const r = scenario.result
  const title = r?.title || r?.basicInfo?.title || scenario.title || 'タイトルなし'

  // 表示するセクション（metadata以外）
  const sections = r ? Object.entries(r).filter(([key, value]) => {
    if (key === 'metadata') return false
    if (value === null || value === undefined) return false
    if (Array.isArray(value) && value.length === 0) return false
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false
    return true
  }) : []

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{toStr(title)}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-4 flex-wrap">
          <Button size="sm" variant="outline" onClick={handleDownloadText}>
            <Download className="w-4 h-4 mr-1" />テキスト
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownloadJson}>
            <Download className="w-4 h-4 mr-1" />JSON
          </Button>
          <Button size="sm" variant="outline" onClick={handleCopyText}>
            <Copy className="w-4 h-4 mr-1" />コピー
          </Button>
        </div>

        <ScrollArea className="h-[60vh]">
          <div className="space-y-4 pr-4">
            {sections.length === 0 ? (
              <p className="text-gray-500">シナリオデータがありません</p>
            ) : (
              sections.map(([key, value]) => (
                <div key={key} className="border-b pb-3">
                  <h3 className="font-semibold mb-2 text-blue-700">{keyToLabel(key)}</h3>
                  <RenderValue value={value} />
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
